import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';

/**
 * OutstandingPayablesWidget — shows top suppliers with unpaid PO balances.
 * Grouped by supplier currency (since rates vary) and grand-totalled per
 * currency at the bottom. Tap any row → jumps to Supplier Payments with
 * that supplier pre-filtered.
 */
const fmt = (n, ccy) =>
  `${ccy} ${(parseFloat(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 3 })}`;

const OutstandingPayablesWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get('/supplier-payments/outstanding-payables?limit=6');
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-slate-200 p-4 mb-4" data-testid="outstanding-payables-widget">
        <div className="text-sm text-slate-500">Loading payables…</div>
      </div>
    );
  }

  const items = data?.top || [];
  const totals = data?.grand_total_by_currency || [];
  const hasNone = items.length === 0;

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4 mb-4" data-testid="outstanding-payables-widget">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-sm font-semibold text-slate-900">🏦 Outstanding Payables</div>
          <div className="text-[11px] text-slate-500">Unpaid PO balances grouped by supplier &amp; currency.</div>
        </div>
        <Link to="/supplier-payments" className="text-xs px-2 py-1 rounded border border-emerald-600 text-emerald-700 hover:bg-emerald-50" data-testid="payables-cta">+ Record Payment</Link>
      </div>

      {hasNone ? (
        <div className="text-sm text-slate-500 py-4 text-center">No outstanding supplier dues 🎉</div>
      ) : (
        <>
          <div className="space-y-1.5">
            {items.map((r) => (
              <div key={`${r.supplier_id}-${r.currency}`} className="flex items-center justify-between border-l-4 border-amber-400 bg-amber-50/40 rounded px-2 py-1.5 text-sm" data-testid={`payable-row-${r.supplier_id}-${r.currency}`}>
                <div className="min-w-0">
                  <div className="font-medium text-slate-900 truncate" title={r.supplier_name}>{r.supplier_name}</div>
                  <div className="text-[10px] text-slate-500">{r.po_count} PO{r.po_count === 1 ? '' : 's'} pending</div>
                </div>
                <div className="text-right text-amber-800 font-semibold font-mono whitespace-nowrap">
                  {fmt(r.outstanding, r.currency)}
                </div>
              </div>
            ))}
          </div>

          {/* Grand totals — per currency, since FX varies */}
          <div className="mt-3 pt-3 border-t border-slate-200 flex flex-wrap gap-2 items-center justify-between">
            <div className="text-[11px] text-slate-500">{data.total_suppliers_with_dues} supplier-currency rows in total</div>
            <div className="flex flex-wrap gap-2">
              {totals.map((t) => (
                <span key={t.currency} className="px-2 py-1 rounded bg-rose-50 border border-rose-200 text-rose-800 text-xs font-semibold" data-testid={`payables-grand-${t.currency}`}>
                  Total {t.currency}: {fmt(t.outstanding, '').trim()}
                </span>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OutstandingPayablesWidget;
